import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiInfo, FiCheckCircle, FiAlertTriangle, FiAlertOctagon, FiX } from 'react-icons/fi';

const TYPE_STYLES = {
  admin_info: {
    Icon: FiInfo,
    iconBg: 'bg-sky-500/20 text-sky-300',
    accent: 'from-sky-400 to-cyan-400',
    ring: 'ring-sky-500/30',
  },
  admin_success: {
    Icon: FiCheckCircle,
    iconBg: 'bg-emerald-500/20 text-emerald-300',
    accent: 'from-emerald-400 to-teal-400',
    ring: 'ring-emerald-500/30',
  },
  admin_warning: {
    Icon: FiAlertTriangle,
    iconBg: 'bg-amber-500/25 text-amber-300',
    accent: 'from-amber-400 to-yellow-400',
    ring: 'ring-amber-500/40',
  },
  admin_error: {
    Icon: FiAlertOctagon,
    iconBg: 'bg-red-500/20 text-red-300',
    accent: 'from-red-400 to-rose-500',
    ring: 'ring-red-500/30',
  },
};

const DEFAULT_STYLE = TYPE_STYLES.admin_info;

function resolveStyle(type) {
  const key = String(type || '');
  return TYPE_STYLES[key] || DEFAULT_STYLE;
}

/**
 * Modale plein écran pour les annonces plateforme (broadcast admin).
 * Design distinct : bandeau or Liberty, verre sombre, typographie claire.
 */
export default function AdminBroadcastModal({ open, title, message, type, onConfirm }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const { Icon, iconBg, accent, ring } = resolveStyle(type);

  const handlePrimary = () => {
    if (typeof onConfirm === 'function') onConfirm();
  };

  const node = (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-broadcast-title"
      aria-describedby="admin-broadcast-desc"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px] transition-opacity"
        aria-label="Fermer l’annonce"
        onClick={handlePrimary}
      />
      <div
        className={`relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-amber-500/35 bg-gradient-to-b from-zinc-900/98 via-zinc-950 to-black shadow-[0_0_0_1px_rgba(251,191,36,0.12),0_25px_80px_-12px_rgba(0,0,0,0.85),0_0_60px_-15px_rgba(245,158,11,0.25)] ring-1 ${ring}`}
      >
        <div className={`h-1.5 w-full bg-gradient-to-r ${accent}`} />
        <div
          className="pointer-events-none absolute -right-16 -top-24 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-20 -left-12 h-40 w-40 rounded-full bg-amber-400/5 blur-2xl"
          aria-hidden
        />

        <button
          type="button"
          onClick={handlePrimary}
          className="absolute right-3 top-3 rounded-full p-2 text-zinc-500 transition hover:bg-white/5 hover:text-zinc-200"
          aria-label="Fermer"
        >
          <FiX className="h-5 w-5" />
        </button>

        <div className="relative px-6 pb-8 pt-10 sm:px-8 sm:pt-12">
          <div className="mb-5 flex flex-col items-center text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200/95">
              Liberty — annonce officielle
            </span>
            <div
              className={`flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 ${iconBg}`}
            >
              <Icon className="h-8 w-8" strokeWidth={1.75} />
            </div>
          </div>

          <h2
            id="admin-broadcast-title"
            className="text-center text-xl font-bold leading-snug text-white sm:text-2xl"
          >
            {title || 'Annonce'}
          </h2>
          <p
            id="admin-broadcast-desc"
            className="mt-4 text-center text-sm leading-relaxed text-zinc-300 sm:text-[15px]"
          >
            {message || ''}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={handlePrimary}
              className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 px-6 text-sm font-semibold text-zinc-900 shadow-lg shadow-amber-900/40 transition hover:from-amber-400 hover:to-yellow-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900 sm:w-auto sm:min-w-[200px]"
            >
              J’ai compris
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}

export function isAdminBroadcastNotification(n) {
  if (!n) return false;
  const t = String(n.type || '');
  if (t.startsWith('admin_')) return true;
  const d = n.data || n.payload || {};
  return d.source === 'admin_broadcast';
}
